"use client";

import { useEffect, useState } from "react";

function BugIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={18}
            height={18}
        >
            <path d="m8 2 1.88 1.88" />
            <path d="M14.12 3.88 16 2" />
            <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
            <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
            <path d="M12 20v-9" />
            <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
            <path d="M6 13H2" />
            <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
            <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
            <path d="M22 13h-4" />
            <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
        </svg>
    );
}

export function BugReportButton({
    variant = "link",
}: {
    variant?: "link" | "sidenav";
} = {}) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">(
        "idle"
    );
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        function onKey(e: KeyboardEvent) {
            if (e.key === "Escape") setOpen(false);
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setStatus("submitting");
        setErrorMsg(null);

        try {
            const res = await fetch("/api/bug-reports", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message }),
            });

            if (!res.ok) {
                const data = (await res.json().catch(() => ({}))) as {
                    error?: string;
                };
                throw new Error(data.error ?? "Couldn't send report.");
            }

            setStatus("done");
            setMessage("");
        } catch (err) {
            setErrorMsg(err instanceof Error ? err.message : "Unknown error");
            setStatus("error");
        }
    }

    function close() {
        setOpen(false);
        // Reset shortly so the close animation doesn't flash the success state
        setTimeout(() => {
            setStatus("idle");
            setErrorMsg(null);
        }, 200);
    }

    return (
        <>
            {variant === "sidenav" ? (
                <button
                    type="button"
                    className="app-sidenav__bug"
                    data-nav-label="Report a Bug"
                    onClick={() => setOpen(true)}
                >
                    <BugIcon />
                    <span>Report a Bug</span>
                </button>
            ) : (
                <button
                    type="button"
                    className="bug-report-link"
                    onClick={() => setOpen(true)}
                >
                    Report a bug
                </button>
            )}

            {open && (
                <div
                    className="bug-report-backdrop"
                    onClick={close}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Report a bug"
                >
                    <div
                        className="bug-report-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="bug-report-close"
                            onClick={close}
                            aria-label="Close"
                        >
                            ✕
                        </button>

                        {status === "done" ? (
                            <div className="bug-report-done">
                                <h2 className="bug-report-title">Thanks!</h2>
                                <p className="bug-report-desc">
                                    Your report was sent to the team.
                                </p>
                                <button
                                    type="button"
                                    className="bug-report-btn bug-report-btn--primary"
                                    onClick={close}
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit}>
                                <h2 className="bug-report-title">Report a bug</h2>
                                <p className="bug-report-desc">
                                    Explain the bug: what happened, what you expected,
                                    and any steps to reproduce it.
                                </p>

                                <textarea
                                    className="bug-report-textarea"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Explain this bug…"
                                    required
                                    maxLength={5000}
                                    rows={6}
                                    autoFocus
                                />

                                {errorMsg && (
                                    <p className="bug-report-error">{errorMsg}</p>
                                )}

                                <div className="bug-report-actions">
                                    <button
                                        type="button"
                                        className="bug-report-btn"
                                        onClick={close}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bug-report-btn bug-report-btn--primary"
                                        disabled={
                                            status === "submitting" || !message.trim()
                                        }
                                    >
                                        {status === "submitting" ? "Sending…" : "Send report"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
